import axios from '@/libs/axios'
import router from '@/router'
import i18n from '@/libs/i18n'
import { withTenantKey } from '@/libs/storageKeys'
import { setPendingLoginReload, setStoredAuthRedirectToast } from '@/helpers/authSession'

let isAlreadyFetchingAccessToken = false
let subscribers = []
let isInitialized = false
let refreshTokenPromise = null

function onAccessTokenFetched(accessToken) {
  subscribers = subscribers.filter(callback => callback(null, accessToken))
}

function onAccessTokenFetchFailed(error) {
  subscribers = subscribers.filter(callback => callback(error))
}

function addSubscriber(callback) {
  subscribers.push(callback)
}

function getStoredTokensOrReject(options = {}) {
  const tokens = JSON.parse(localStorage.getItem(withTenantKey('tokens')))
  if (tokens) {
    return tokens
  }

  if (localStorage.getItem(withTenantKey('userInfo'))) {
    localStorage.removeItem(withTenantKey('userInfo'))
  }
  if (localStorage.getItem(withTenantKey('tokens'))) {
    localStorage.removeItem(withTenantKey('tokens'))
  }
  setStoredAuthRedirectToast('auth.session-expired')
  setPendingLoginReload()
  if (!options.skipUnauthorizedHandler) {
    router.push({ name: 'login' })
  }

  throw new Error('Logged Out')
}

function requestRefreshToken(options = {}) {
  if (refreshTokenPromise) {
    return refreshTokenPromise
  }

  const tokens = getStoredTokensOrReject(options)

  refreshTokenPromise = axios.post(
    '/auth/token/refresh',
    { refreshToken: tokens.refreshToken },
    { skipUnauthorizedHandler: !!options.skipUnauthorizedHandler },
  ).finally(() => {
    refreshTokenPromise = null
  })

  return refreshTokenPromise
}

export default {
  init(store) {
    if (!store || isInitialized) {
      return
    }

    isInitialized = true

    axios.interceptors.response.use(response => response, error => {
      const { config, response } = error
      const originalRequest = config
      const isUnauthorized = !!response && response.status === 401
      const requestUrl = originalRequest?.url || ''
      const isRefreshRequest = requestUrl.includes('/auth/token/refresh')
      const isAuthRequest = ['/auth/token', '/auth/2fa_check'].includes(requestUrl)
      const isLoginRoute = router.currentRoute?.name === 'login'
      const requestAuthorizationHeader = originalRequest?.headers?.Authorization || originalRequest?.headers?.authorization
      const hasSession = (typeof store.state.auth?.isUserLoggedIn === 'function' && store.state.auth.isUserLoggedIn())
        || !!localStorage.getItem(withTenantKey('tokens'))
      const canRetryAfterRefresh = isUnauthorized
        && hasSession
        && !isRefreshRequest
        && !isAuthRequest
        && !isLoginRoute
        && !!requestAuthorizationHeader
        && !originalRequest?.skipUnauthorizedHandler
        && !originalRequest?.retryAfterRefresh

      if (canRetryAfterRefresh) {
        return new Promise((resolve, reject) => {
          addSubscriber((refreshError, accessToken) => {
            if (refreshError) {
              reject(refreshError)
              return false
            }

            originalRequest.headers = originalRequest.headers || {}
            originalRequest.retryAfterRefresh = true
            originalRequest.headers.Authorization = `Bearer ${accessToken.data.token}`
            resolve(axios(originalRequest))
            return false
          })

          if (!isAlreadyFetchingAccessToken) {
            isAlreadyFetchingAccessToken = true
            store.dispatch('auth/fetchAccessTokenByRefreshToken')
              .then(accessToken => {
                onAccessTokenFetched(accessToken)
                store.dispatch('auth/fetchUserInfo')
              })
              .catch(refreshError => {
                if (refreshError?.response) {
                  this.handleUnauthorizedResponse(store, refreshError.response)
                }
                onAccessTokenFetchFailed(refreshError)
              })
              .finally(() => {
                isAlreadyFetchingAccessToken = false
              })
          }
        })
      }

      if (isUnauthorized && !originalRequest?.skipUnauthorizedHandler) {
        this.handleUnauthorizedResponse(store, response)
      }

      if (response) {
        const normalizedError = new Error(response.data?.message || response.statusText || 'Request error')
        normalizedError.response = response
        normalizedError.status = response.status
        normalizedError.statusText = response.statusText
        normalizedError.headers = response.headers
        if (response.data && typeof response.data === 'object') {
          Object.assign(normalizedError, response.data)
        }
        return Promise.reject(normalizedError)
      }

      let translatedMessage = 'Unknown connection error'
      if (error.message) {
        translatedMessage = i18n.t(error.message)
      }
      if (error.message && error.code && Number(error.code) === 500) {
        translatedMessage = `Błąd serwera: ${translatedMessage}`
      }

      const normalizedError = new Error(translatedMessage)
      normalizedError.originalError = error
      return Promise.reject(normalizedError)
    })
  },
  handleUnauthorizedResponse(store, response) {
    const message = response?.data?.message
    const logoutErrorMessages = ['An authentication exception occurred.', 'Invalid JWT Refresh Token', 'JWT Refresh Token Not Found']
    const isMissingToken = message === 'JWT Token not found'
    const isLoginRoute = router.currentRoute?.name === 'login'
    const hasSession = (typeof store.state.auth?.isUserLoggedIn === 'function' && store.state.auth.isUserLoggedIn())
      || !!localStorage.getItem(withTenantKey('tokens'))

    if (!logoutErrorMessages.includes(message) && !(isMissingToken && hasSession)) {
      return
    }

    if (!isLoginRoute) {
      localStorage.setItem('previousPath', router.currentRoute.path)
    }

    setStoredAuthRedirectToast('auth.session-expired')
    setPendingLoginReload()

    if (hasSession) {
      store.dispatch('auth/logout', { reason: 'expired' }).then(() => router.push({ name: 'login' }))
      return
    }

    if (!isLoginRoute) {
      router.push({ name: 'login' })
    }
  },
  login(username, password) {
    return axios.post('/auth/token', {
      username,
      password,
    })
  },
  twoFactorCheck(payload) {
    return axios.post('/auth/2fa_check', payload)
  },
  logout() {
    return axios.post('/auth/logout')
  },
  userInfo() {
    return axios.get('/user/me')
  },
  userCurrentTeam() {
    return axios.get('/user/me/current-team')
  },
  userNotifications() {
    return axios.get('/user/me/notifications')
  },
  changeStatus: (id, data) => axios.patch(`/team/${id}/change-status`, data),
  markNotificationAsRead: id => axios.get(`/user/notification/read/${id}`),
  refreshToken(options = {}) {
    return Promise.resolve().then(() => requestRefreshToken(options))
  },
}
